import axios from 'axios';
import { useEffect, useState } from 'react';
import { STOREFRONT_API_KEY, STOREFRONT_API_URL } from '../constants';

// クーポンコード（小文字）→ 説明文 のマッピング
type CouponMap = Map<string, string>;

// メタオブジェクト coupon_description の type 名
// （app-custom のチェックアウト拡張 couponDescriptionInfo.tsx と共通）
const COUPON_METAOBJECT_TYPE = 'coupon_description';

// 定期便カードごとに重複取得しないよう、取得結果をモジュールレベルでキャッシュする
let cachedPromise: Promise<CouponMap> | null = null;

const fetchCouponDescriptions = async (): Promise<CouponMap> => {
  // 環境変数が未設定の環境では通信せず空 Map を返す（無駄な通信失敗・console error を避ける）
  if (!STOREFRONT_API_URL || !STOREFRONT_API_KEY) {
    return new Map();
  }

  // first は Storefront API の上限 250 まで取得する。
  // 将来クーポン説明が 250 件を超える運用になった場合はページネーション対応が必要。
  const response = await axios.post(
    STOREFRONT_API_URL,
    {
      query: `
        query {
          metaobjects(type: "${COUPON_METAOBJECT_TYPE}", first: 250) {
            edges {
              node {
                fields {
                  key
                  value
                }
              }
            }
          }
        }
      `,
    },
    {
      headers: {
        'X-Shopify-Storefront-Access-Token': STOREFRONT_API_KEY,
      },
    },
  );

  const map: CouponMap = new Map();
  const edges = response.data?.data?.metaobjects?.edges ?? [];
  for (const { node } of edges) {
    const fields: { key: string; value: string }[] = node.fields;
    const code = fields.find((f) => f.key === 'code')?.value ?? '';
    const description = fields.find((f) => f.key === 'description')?.value ?? '';
    // コードは大文字小文字を無視して突き合わせる（チェックアウト拡張と同じ突合）
    if (code && description) map.set(code.trim().toLowerCase(), description);
  }

  return map;
};

/**
 * クーポンコード → 説明文 のマッピングを Storefront API のメタオブジェクトから取得する。
 *
 * - 取得はモジュールレベルでキャッシュするため、画面内で複数回呼んでも通信は1回に抑えられる。
 * - 入力中の突合はこのマップへの O(1) 参照のみで完結し、キーストロークごとの通信は発生しない。
 */
export const useCouponDescription = () => {
  const [couponMap, setCouponMap] = useState<CouponMap>(new Map());

  useEffect(() => {
    let aborted = false;

    if (!cachedPromise) {
      cachedPromise = fetchCouponDescriptions().catch((error) => {
        // 失敗時はキャッシュを破棄し、次回マウント時に再取得できるようにする
        cachedPromise = null;
        console.error('クーポン説明の取得に失敗しました', error);
        return new Map<string, string>();
      });
    }

    cachedPromise.then((map) => {
      if (!aborted) setCouponMap(map);
    });

    return () => {
      aborted = true;
    };
  }, []);

  return { couponMap };
};
